import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  createNoteAPI,
  deleteNoteAPI,
  getNotesForTeamYearAPI,
  updateNoteAPI,
} from '@/services/admin';
import { NoteDTO, type INoteDTO } from '@/dtos';
import { useUpdateStatusStore } from '../updateStatus';

export const useNoteStore = defineStore('note', () => {
  const queryClient = useQueryClient();
  const teamCriteriaId = ref<string | undefined>(undefined);
  const notesQueryKey = computed(() => ['notes', teamCriteriaId]);
  const statusStore = useUpdateStatusStore();
  const notesQuery = useQuery({
    queryKey: notesQueryKey,
    queryFn: async (): Promise<INoteDTO[]> => {
      if (!teamCriteriaId.value) throw new Error('No teamCriteriaId set');
      const ret = await getNotesForTeamYearAPI(teamCriteriaId.value);
      const notes = await Promise.all<INoteDTO[]>(ret.data.map((note) => new NoteDTO(note)));
      return notes;
    },
    enabled: () => !!teamCriteriaId.value && teamCriteriaId.value != '', // Don't run until teamCriteriaId is set
  });

  // Mutations
  const create = useMutation({
    mutationFn: (note: INoteDTO) => {
      if (!teamCriteriaId.value) throw new Error('No teamCriteriaId set');
      return createNoteAPI(teamCriteriaId.value, note);
    },
    onSuccess: () => {
      if (teamCriteriaId.value) {
        queryClient.invalidateQueries({ queryKey: notesQueryKey.value });
      }
      statusStore.setSuccessStatus('note-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`note-create`, err);
    },
  });

  const update = useMutation({
    mutationFn: (note: INoteDTO) => {
      return updateNoteAPI(note);
    },
    onSuccess: (_data, note: INoteDTO) => {
      if (teamCriteriaId.value) {
        queryClient.invalidateQueries({ queryKey: notesQueryKey.value });
      }
      statusStore.setSuccessStatus(`note-${note.id}`, 'success:update');
    },
    onError: (err: string, noteId) => {
      statusStore.setErrorStatus(`note-${noteId}`, err);
    },
  });

  const remove = useMutation({
    mutationFn: (noteId: string) => deleteNoteAPI(noteId),
    onSuccess: (_data, noteId: string) => {
      if (teamCriteriaId.value) {
        queryClient.invalidateQueries({ queryKey: notesQueryKey.value });
      }
      statusStore.setSuccessStatus(`note-${noteId}`, 'success:delete');
    },
    onError: (err: string, noteId: string) => {
      statusStore.setErrorStatus(`note-${noteId}`, err);
    },
  });

  return {
    teamCriteriaId,
    notesQuery,
    create,
    update,
    remove,
  };
});
